import { useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./DefaultInputField.module.scss";
import { Controller, useFormContext } from "react-hook-form";
import { useDefaultInputField } from "./hooks/useDefaultInputField";
import { useCurrencyDropdown } from "./hooks/useCurrencyDropdown";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";
import { CurrencyType } from "../../../../enums";
import { CurrencyTypeSymbols } from "../../../../labels";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDefaultInputField {
  className?: string;
  name: string;
  tooltip?: string;
  placeholder: string;
  label?: string;
  required?: boolean;
  type?: FieldType;
  step?: number;
  width?: string;
  minWidth?: string;
  min?: number;
  max?: number;
  currency?: CurrencyType | CurrencyType[];
  currencyName?: string;
  disabled?: boolean;
  autofocus?: boolean;
  prefix?: string;
  autocomplete?: string;
}

type FieldType = "password" | "text" | "number";

export const DefaultInputField = ({
  className,
  name,
  tooltip,
  placeholder,
  label,
  required = false,
  type = "text",
  step = 1,
  width,
  minWidth,
  min,
  max,
  currency,
  currencyName,
  disabled,
  autofocus = false,
  prefix = "",
  autocomplete,
}: IDefaultInputField) => {
  const { control, setValue } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === "password";
  const inputType = isPasswordField ? (showPassword ? "text" : "password") : type;

  const {
    ref,
    isFocused,
    onFocus,
    onBlur,
    onContainerClick,
    handleInputChange,
    handleKeyDown,
    handleClick,
  } = useDefaultInputField({
    name,
    control,
    prefix,
    autofocus,
    setValue,
  });

  const {
    currencies,
    selectedCurrency,
    showCurrencyDropdown,
    currencyDropdownRef,
    isMultiCurrency,
    handleCurrencyClick,
    handleCurrencySelect,
  } = useCurrencyDropdown({
    currency,
    disabled,
    currencyName,
    setValue,
  });

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div
          style={{ width: width, minWidth: minWidth, maxWidth: width }}
          className={classNames(className, styles.root, {
            [styles.root_disabled]: disabled,
          })}
          onClick={onContainerClick}
        >
          <RenderWhen condition={!!label}>
            <div
              className={classNames(styles.root_label, {
                [styles.root_label_interacted_with]:
                  isFocused ||
                  (field.value &&
                    field.value.length > (prefix?.length || 0)) ||
                  (field.value !== null && field.value !== ""),
                [styles.root_label_error]: !!fieldState.error?.message,
              })}
            >
              {label}
              {required ? "*" : ""}
              <AppTooltip content={tooltip} />
            </div>
          </RenderWhen>
          <div className={styles.root_input_container}>
            <input
              id={field.name}
              type={inputType}
              step={step}
              ref={ref}
              name={name}
              className={styles.root_input}
              placeholder={placeholder}
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={(e) => handleInputChange(e, field.onChange)}
              onKeyDown={handleKeyDown}
              onClick={handleClick}
              min={min}
              max={max}
              disabled={disabled}
              autoComplete={autocomplete}
              value={field.value ?? ""}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
            <RenderWhen condition={isPasswordField}>
              <button
                type="button"
                className={styles.root_password_toggle}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <i className="pi pi-eye-slash" />
                ) : (
                  <i className="pi pi-eye" />
                )}
              </button>
            </RenderWhen>
            <RenderWhen condition={currencies.length > 0}>
              <div className={styles.root_currency_wrapper} ref={currencyDropdownRef}>
                <span
                  onClick={handleCurrencyClick}
                  className={classNames(styles.root_currency_selector, {
                    [styles.root_currency_selector_clickable]: isMultiCurrency && !disabled,
                    [styles.root_currency_selector_disabled]: !isMultiCurrency || disabled,
                  })}
                >
                  {selectedCurrency !== undefined ? CurrencyTypeSymbols[selectedCurrency] : ""}
                  {isMultiCurrency && !disabled && (
                    <span
                      className={classNames(styles.root_currency_arrow, {
                        [styles.root_currency_arrow_open]: showCurrencyDropdown,
                        [styles.root_currency_arrow_closed]: !showCurrencyDropdown,
                      })}
                    >
                      ▼
                    </span>
                  )}
                </span>
                {showCurrencyDropdown && isMultiCurrency && (
                  <div className={styles.root_currency_dropdown}>
                    {currencies.map((curr) => (
                      <div
                        key={curr}
                        onClick={() => handleCurrencySelect(curr)}
                        className={classNames(styles.root_currency_item, {
                          [styles.root_currency_item_selected]: curr === selectedCurrency,
                        })}
                      >
                        {CurrencyTypeSymbols[curr]}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </RenderWhen>
          </div>
        </div>
      )}
    />
  );
};
